import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setAhuTabAccordion } from 'app/slices/ahuBoardSlice';
import {
  ahuBoardFormsApi,
  usePatchAhuUnitMutation,
  useGetRatedVa115Query,
  useGetRatedVa24Query,
  useGetConstructionMethodsQuery,
  useGetFramingSettingsQuery,
  useGetMasterVoltagesQuery,
  useGetRoofShapesQuery,
  useGetRoofLayersQuery,
  useGetRoofSeamDirectionsQuery,
  useGetRoofSlopeOrientationsQuery,
  useGetRoofSlopeDirectionsQuery,
  useGetAhuCertificationsQuery,
  usePutAhuCertificationMutation,
  useDeleteAhuCertificationMutation,
} from 'app/services/ahuBoardFormsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import type {
  IAhuBoardSection,
  IAhuBoardTunnel,
  IAhuBoardUnit,
  ICmsAhuDoc,
  IConstructionClass,
} from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  validateNumberField,
  selectValueToId,
  useDebouncedCallback,
  getContenteditableHTML,
  formatLegacyNumber,
} from '../../forms/formUtils';
import {
  cmsWeatherRatings,
  cmsAirSystems,
  cmsSaVolumeControls,
  cmsConfigurations,
  cmsCertifications,
} from '../../forms/cmsDoc';
import TunnelCabinetRow from './TunnelCabinetRow';
import LiftingLugPane from './LiftingLugPane';
import BasePanel from './BasePanel';
import { compareTunnelAddress, formatTunnelAddress, formatRowLevel } from '../../addressing';

/**
 * Phase 6e1 — AHU tab (§7.3, view/tabs/tab.ahu.js + tab.ahu.tmpl, markup
 * faithful): 9-group single-open accordion with per-field saves. Persistence
 * is store-driven (patchAhuUnit; the AHU model's §19.2 cascade reproduced in
 * saveFields below: success → `get:overall:height`; construction_class_id →
 * window.location.reload()).
 *
 * Accordion state lives in ahuBoard.ahuTab.openAccordion (§13 rows 3/13/15 —
 * canvas/lug senders write 'tunnel:<id>' / 'tunnel:first' / 'lugs').
 *
 * [NEG]s honored: #41 (overall_height refreshes via PATCH/refetch responses,
 * the dead `#ahu_overall_height` write is not ported), #49 ($('document')
 * no-op dropped), #51 (no module-scope hasRating).
 */

interface TabAhuProps {
  ahu: IAhuBoardUnit;
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
  constructionClasses: IConstructionClass[] | undefined;
  mfgId: number;
  isPowerUser: boolean;
}

const TabAhu = ({
  ahu,
  tunnels,
  sections,
  cms,
  constructionClasses,
  mfgId,
  isPowerUser,
}: TabAhuProps) => {
  const dispatch = useAppDispatch();
  const openAccordion = useAppSelector((s) => s.ahuBoard.ahuTab.openAccordion);
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  // Hybrid AHUs: Base & Lifting Lugs have no independent tunnel picker —
  // both follow the AHU-wide selected section (§7.3 Base &amp; Lugs split).
  const hybridActiveTunnelId = useMemo(() => {
    const section = sections.find((s) => s.id === currentSectionId);
    return section?.tunnel_id ?? tunnels[0]?.id ?? null;
  }, [sections, currentSectionId, tunnels]);
  // §13 rows 6/7 — the per-component open gate (SelectionMode.ratingModeActive),
  // read by the Special Options SELECT toggle below.
  const ratingOpen = useAppSelector((s) => s.ahuBoard.rating.open);
  const [patchAhuUnit] = usePatchAhuUnitMutation();
  const [putCert] = usePutAhuCertificationMutation();
  const [deleteCert] = useDeleteAhuCertificationMutation();

  const [certListOpen, setCertListOpen] = useState(false);
  const [tagNote, setTagNote] = useState<'min' | 'max' | null>(null);
  const certGroupRef = useRef<HTMLLIElement | null>(null);
  const scrollerRef = useRef<HTMLUListElement | null>(null);

  const ccid = ahu.construction_class_id ?? 1;
  const { data: methods } = useGetConstructionMethodsQuery(ccid);
  const { data: framing } = useGetFramingSettingsQuery();
  const { data: voltages } = useGetMasterVoltagesQuery();
  const { data: roofShapes } = useGetRoofShapesQuery();
  const { data: roofLayers } = useGetRoofLayersQuery();
  const { data: roofSeamDirections } = useGetRoofSeamDirectionsQuery();
  const { data: ratedVa115 } = useGetRatedVa115Query(ahu.id);
  const { data: ratedVa24 } = useGetRatedVa24Query(ahu.id);
  const { data: certifications } = useGetAhuCertificationsQuery(ahu.id);
  const roofStyleId = (ahu.roof_slope_style_id as number | null | undefined) ?? null;
  const roofOrientationId =
    (ahu.roof_slope_orientation_id as number | null | undefined) ?? null;
  const { data: roofOrientations } = useGetRoofSlopeOrientationsQuery(
    roofStyleId != null ? { styleId: roofStyleId } : skipToken
  );
  // Legacy only fetches directions once BOTH style and orientation are set
  // (the empty-orientation call 500s — verify 6e2a-11).
  const { data: roofDirections } = useGetRoofSlopeDirectionsQuery(
    roofStyleId != null && roofOrientationId != null
      ? { styleId: roofStyleId, orientationId: roofOrientationId }
      : skipToken
  );

  // ---- save plumbing (§7.3 changeField → model.set → PATCH + cascades) ----
  const saveFields = (fields: Record<string, unknown>) => {
    patchAhuUnit({ id: ahu.id, data: fields })
      .unwrap()
      .then(() => {
        ahuBoardBus.emit('get:overall:height');
        if ('construction_class_id' in fields) window.location.reload();
      })
      .catch(() => undefined);
  };

  // QA6 #2 — ONE PATCH PER EDIT, via the browser's own `change` semantics.
  //
  // Legacy saves an input two ways, both routed to `changeField`: the native
  // `change` event (tab.ahu.js:51-52) and a 1 s keyup debounce (:73 → :200
  // `_.debounce(this.changeField, 1000)`). React mirrors both (onBlur +
  // debounced onInput), but where Backbone's `model.set` silently drops a set
  // whose value already matches the model — so one edit produces exactly one
  // PATCH and re-committing an unchanged field produces none — React re-sent
  // the same body twice (observed live: two identical {"ra_volume_max":…}).
  //
  // We reproduce legacy's *trigger* rather than guessing at the stored value: a
  // commit is skipped when the field's raw text has not changed since it was
  // focused (exactly when the browser would not fire `change`), and the raw
  // text is re-baselined after every commit so the debounced-input save and the
  // blur save of a single edit collapse to one request. An earlier attempt
  // deduped against a *snapshot* of the loaded unit instead; that silently
  // swallowed a legitimate re-commit whenever the stored value had moved on
  // underneath the tab (legacy PATCHes there — its model is refreshed by the
  // syncdata socket, `syncdata_listener.js` onPut → `set(data,
  // {__from_back_end:true})` — while the snapshot could not know).
  const rawBaseline = useRef(new WeakMap<Element, string>());
  const readRaw = (el: Element) =>
    el instanceof HTMLInputElement ? el.value : (el as HTMLElement).innerHTML;
  const noteFocus = (el: EventTarget | null) => {
    if (el instanceof HTMLElement) rawBaseline.current.set(el, readRaw(el));
  };
  const changedSinceFocus = (el: Element) => rawBaseline.current.get(el) !== readRaw(el);
  const rebase = (el: Element) => {
    rawBaseline.current.set(el, readRaw(el));
  };
  /** Commit an input-driven save only if a native `change` would have fired. */
  const commitInput = (el: Element, run: () => void) => {
    if (!changedSinceFocus(el)) return;
    run();
    rebase(el);
  };

  // QA5 #2 — takes the INPUT ELEMENT (not the synthetic event): the debounced
  // keyup path used to dereference e.currentTarget after React nulled it
  // (TypeError observed live), so typing never saved. After validation the
  // display is re-formatted like legacy validateNumberField does on every
  // change/render (decimal → toFixed(2)+comma quirk, integer → round+commas).
  const onNumberField = (input: HTMLInputElement) => {
    // QA6 #2 — no native `change` would fire, so legacy's changeField never
    // runs: skip (this is what makes focus+blur with no edit send nothing, and
    // what collapses the debounced-input save + blur save of one edit into one
    // request — the display is re-baselined at the end of every commit).
    if (!changedSinceFocus(input)) return;
    const min = input.dataset.min != null ? Number(input.dataset.min) : null;
    const max = input.dataset.max != null ? Number(input.dataset.max) : null;
    const step = input.dataset.step != null ? Number(input.dataset.step) : null;
    const name = input.name;
    // QA6 #2 — CLEARING A FIELD SAVES null, never a substituted min/max.
    //
    // The old code here implemented "§7.3 item 6 empty-value substitution"
    // (6e1, 2026-06-12) and wrote max_airflow for *_volume_max, min_airflow
    // for *_volume_min. That misread legacy. Legacy's changeField
    // (tab.ahu.js:447-492) runs `value = self.validateNumberField(elem)`,
    // which returns **undefined** for an empty input (:632 — `input.val()
    // .length ? Number(...) : undefined`), and its first branch is
    // `if (value == null)` (:458) — and `undefined == null` is TRUE, so an
    // empty field ALWAYS takes that branch and saves null. The min/max
    // substitution branch below it (:462-489) is reachable only when value is
    // NaN (i.e. the box holds non-numeric text), never when it is empty.
    // Verified live on :8130 vs :8005: clearing ra_volume_max sent
    // {"ra_volume_max":null} on legacy but {"ra_volume_max":400000} on React
    // — a silent 400,000 CFM write (min_airflow/max_airflow are not even
    // columns on ksdm_ahu, so `Number(undefined) || 400000` always won).
    if (input.value.trim() === '') {
      saveFields({ [name]: null });
      rebase(input);
      return;
    }
    const res = validateNumberField(input.value, { min, max, mode: 'clamp', step });
    if (!res.ok) return;
    input.value = res.value == null ? '' : formatLegacyNumber(res.value, step);
    saveFields({ [name]: res.value });
    rebase(input);
  };

  const debouncedNumberField = useDebouncedCallback(onNumberField, 1000);

  // QA5 #2 — Module Max W×H×L parity: legacy routes these through
  // changeConstructionMethodField (tab.ahu.js:368-395), which parseInt()s the
  // value (decimals TRUNCATE) and clamps against parseInt(data-min/max)
  // before saving; the visible "600.00" style then comes from the render-time
  // validateNumberField pass (data-step 0.01 → toFixed(2)). No keyup save in
  // legacy for these fields (.cnstr_field has no keyup binding).
  const onConstructionMethodField = (input: HTMLInputElement) => {
    const name = input.name;
    if (!changedSinceFocus(input)) return; // QA6 #2 — see onNumberField
    if (input.value.trim() === '') return;
    let value = parseInt(input.value.replace(/,/g, ''), 10);
    if (Number.isNaN(value)) return;
    const min = parseInt(input.dataset.min ?? '', 10);
    const max = parseInt(input.dataset.max ?? '', 10);
    if (!Number.isNaN(max) && value > max) value = max;
    else if (!Number.isNaN(min) && value < min) value = min;
    input.value = formatLegacyNumber(value, 0.01);
    saveFields({ [name]: value });
    rebase(input);
  };

  const onSelectField = (e: React.ChangeEvent<HTMLSelectElement>) => {
    saveFields({ [e.currentTarget.name]: selectValueToId(e.currentTarget.value) });
  };

  // ---- accordion (single-open; §7.3 subtabOpenClose) ----
  const toggle = (key: string) => {
    const next = openAccordion === key ? null : key;
    const scrollTop = scrollerRef.current?.scrollTop ?? 0;
    dispatch(setAhuTabAccordion(next));
    requestAnimationFrame(() => {
      if (scrollerRef.current) scrollerRef.current.scrollTop = scrollTop;
    });
  };

  // First render auto-opens the selected (or first) tunnel (parity 108).
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || openAccordion != null || !tunnels.length) return;
    autoOpened.current = true;
    const currentTunnel = sections.find((s) => s.id === currentSectionId)?.tunnel_id;
    dispatch(setAhuTabAccordion(`tunnel:${currentTunnel ?? tunnels[0].id}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tunnels.length]);

  // 'tunnel:first' sender resolution (§13 row 15).
  const resolvedAccordion =
    openAccordion === 'tunnel:first' && tunnels.length
      ? `tunnel:${tunnels[0].id}`
      : openAccordion;

  const isOpen = (key: string) => resolvedAccordion === key;

  // ---- tunnel list ordering + hybrid "Row:level" grouping (tab.ahu.js:395-485)
  //
  // Legacy builds `current_indexes` from the tunnel collection sorted by
  // (row, level, position) — NOT the API's `ordering = ['id']` insertion
  // order — so the Data Bar reads 1:1:1 → 1:1:2 → 1:2:1 → 2:1:1 however the
  // tunnels were created.
  const orderedTunnels = useMemo(() => [...tunnels].sort(compareTunnelAddress), [tunnels]);

  // Hybrid AHUs only: tunnels sharing (row, level) are bucketed under one
  // "Row:level {row}:{level}" collapsible, and the tunnel inside it is
  // labelled with its bare 3-field address ("1:1:1"), not "Tunnel 1:1:1".
  // Non-hybrid AHUs keep the flat "Tunnel {row}:{level}" list unchanged
  // (tab.ahu.tmpl:17-69).
  const tunnelRowGroups = useMemo(() => {
    if (!ahu.is_hybrid) return [];
    const groups = new Map<string, { key: string; label: string; id: string; tunnels: IAhuBoardTunnel[] }>();
    for (const tunnel of orderedTunnels) {
      const key = formatRowLevel(tunnel);
      if (!groups.has(key)) {
        const [row, level] = key.split(':');
        groups.set(key, {
          key,
          label: `Row:level ${row}:${level}`,
          id: `tunnel_row_${row}_${level}`,
          tunnels: [],
        });
      }
      groups.get(key)!.tunnels.push(tunnel);
    }
    return [...groups.values()];
  }, [ahu.is_hybrid, orderedTunnels]);

  // `expanded_tunnel_rows` (tab.ahu.js:99) — which row groups are open. Kept
  // separate from the single-open accordion above: several rows can be
  // expanded at once, and a row can be collapsed while the tunnel subtab
  // inside it is still checked.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => new Set());
  const toggleRowGroup = (key: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  // Legacy does this in three places (subtabOpenClose:332-343, the canvas
  // section-select listener:250-264, and the first-render is_expanded
  // fallback:470-475): whenever a tunnel subtab becomes the checked one, its
  // row group is expanded too — otherwise the subtab is checked but hidden
  // behind a collapsed group. Watching the resolved accordion covers all of
  // them at once (user click, the first-render auto-open, and the canvas /
  // bus senders that write `tunnel:<id>` straight into the store).
  useEffect(() => {
    if (!ahu.is_hybrid) return;
    const match = /^tunnel:(\d+)$/.exec(resolvedAccordion ?? '');
    if (!match) return;
    const tunnel = tunnels.find((t) => String(t.id) === match[1]);
    if (!tunnel) return;
    const key = formatRowLevel(tunnel);
    setExpandedRows((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, [resolvedAccordion, tunnels, ahu.is_hybrid]);

  /** One `li.tunnel_li` — identical markup in the flat and grouped branches
   *  apart from the label, which drops the "Tunnel " prefix inside a group
   *  because the group header already reads "Row:level {row}:{level}". */
  const renderTunnelLi = (tunnel: IAhuBoardTunnel, idx: number) => (
    <li
      key={tunnel.id}
      id={`tunnel_${tunnel.index_id ?? idx}`}
      className={`tunnel_li tunnel_li_${idx}`}
    >
      <ul className="inputs-group">
        <li>
          <input
            id={`tunnel_${tunnel.id}`}
            className="subtab"
            type="checkbox"
            name="ahu_left_bar"
            checked={isOpen(`tunnel:${tunnel.id}`)}
            onChange={() => toggle(`tunnel:${tunnel.id}`)}
          />
          <label htmlFor={`tunnel_${tunnel.id}`}>
            {ahu.is_hybrid ? formatTunnelAddress(tunnel, true) : `Tunnel ${tunnel.index}`}
            <i className="icon-add" />
          </label>
          {/* Always mounted (legacy renders into the sublist regardless
              of open state): li.tunnel-dimensions-li is absolutely
              positioned into the HEADER line by styles.css:5503, so the
              W×H×L controls must show even when the row is collapsed. */}
          <ul className="sublist" id={`tunnel_${tunnel.id}_content`}>
            <TunnelCabinetRow
              ahu={ahu}
              tunnel={tunnel}
              tunnels={tunnels}
              cms={cms}
              mfgId={mfgId}
              isPowerUser={isPowerUser}
            />
          </ul>
        </li>
      </ul>
    </li>
  );

  // ---- AHU tag (3-30 + notes + live mirror, §7.3 item 1) ----
  const onTagInput = (e: React.FormEvent<HTMLInputElement>) => {
    const v = e.currentTarget.value;
    document
      .querySelectorAll('#ahu-name span.ahu-tag')
      .forEach((el) => {
        el.textContent = v;
      });
  };
  /** dev-sync F (#823) — legacy `checkTagValidity` (tab.ahu.js): tooShort /
   *  tooLong drive the two notes AND an `invalid-field` class on the input;
   *  the return value gates the save. */
  const tagValidity = (value: string) => ({
    tooShort: value.length < 3,
    tooLong: value.length > 30,
  });
  const applyTagValidity = (value: string) => {
    const { tooShort, tooLong } = tagValidity(value);
    setTagNote(tooShort ? 'min' : tooLong ? 'max' : null);
    return !tooShort && !tooLong;
  };
  const commitTag = (value: string) => {
    if (value.length < 3) {
      setTagNote('min');
      return;
    }
    if (value.length > 30) {
      setTagNote('max');
      return;
    }
    setTagNote(null);
    saveFields({ tag: value });
    const ratingTag = document.querySelector<HTMLInputElement>(
      '#component_rating_mode input.ahu_tag'
    );
    if (ratingTag) ratingTag.value = value;
  };
  // QA6 #2 — the debounced keyup save and the blur save must collapse to one
  // PATCH, so the debounced path commits through the same change-gate.
  const debouncedTag = useDebouncedCallback(
    (input: HTMLInputElement) => commitInput(input, () => commitTag(input.value)),
    1000
  );

  // ---- certifications commit-on-collapse (§7.3 item 5) ----
  const certCommit = () => {
    if (!certGroupRef.current) return;
    const cmsCerts = cmsCertifications(cms);
    const current = certifications ?? [];
    const inputs = certGroupRef.current.querySelectorAll<HTMLInputElement>(
      'input.certification'
    );
    inputs.forEach((input) => {
      const id = Number(input.value);
      const row = cmsCerts.find((c) => c.id === id);
      const has = current.some((c) => c.id === id);
      if (input.checked && !has) {
        void putCert({ ahuId: ahu.id, certId: id, name: String(row?.name ?? '') });
      } else if (!input.checked && has) {
        void deleteCert({ ahuId: ahu.id, certId: id });
      }
    });
    setCertListOpen(false);
  };
  useEffect(() => {
    if (!certListOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (certGroupRef.current && !certGroupRef.current.contains(e.target as Node)) {
        certCommit();
      }
    };
    document.addEventListener('click', onDoc);
    const off = ahuBoardBus.on('certifications:save', certCommit);
    return () => {
      document.removeEventListener('click', onDoc);
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certListOpen, certifications]);

  // `update:ahu:data` (P4 → ta model.set) → PATCH path is the cache write.
  useEffect(() => {
    const off = ahuBoardBus.on('update:ahu:data', (data) => {
      dispatch(
        ahuBoardFormsApi.util.updateQueryData('getAhuUnit', ahu.id, (draft) =>
          Object.assign(draft, data)
        )
      );
    });
    return off;
  }, [ahu.id, dispatch]);

  // ---- remarks (1000 cap, blur-only commit §7.3 item 9) ----
  const onRemarksBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // QA6 #2 — legacy commits remarks on blur through the same model.set, so an
    // untouched blur sends nothing (Backbone drops the unchanged set).
    const el = e.currentTarget;
    commitInput(el, () => saveFields({ remarks: getContenteditableHTML(el, 1000) }));
  };

  // ---- construction method auto-snap (§7.3 item 3) ----
  // Legacy does a LOCAL model.set only (tab.ahu.js:224-229) — NO PATCH. The
  // verify-flagged one-shot `PATCH ahu/units/<id>` on first load came from
  // saving here; write the RTKQ cache instead (display-only snap).
  useEffect(() => {
    if (!methods?.length) return;
    const currentId = ahu.ahu_construction_method_id;
    if (currentId == null || !methods.some((m) => m.id === currentId)) {
      dispatch(
        ahuBoardFormsApi.util.updateQueryData('getAhuUnit', ahu.id, (draft) => {
          (draft as Record<string, unknown>).ahu_construction_method_id =
            methods[0].id;
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods]);

  const framingDims = (framing ?? {}) as Record<string, number | undefined>;
  const currentClass = constructionClasses?.find((c) => c.id === ahu.construction_class_id);
  const weatherRatings = cmsWeatherRatings(cms);
  const airSystems = cmsAirSystems(cms);
  const saControls = cmsSaVolumeControls(cms);
  const configurations = cmsConfigurations(cms);
  const cmsCerts = cmsCertifications(cms);
  const roofDisabled = !!ahu.roof_slope_settings;
  const roofRowClass = `roof_construction${roofDisabled ? ' disable-elements' : ''}`;
  const configurationValue = configurations.find((c) => c.id === ahu.configuration_id)
    ?.value;
  const showDecks = configurationValue === 'multizone' || configurationValue === 'dual_duct';

  const selSpan = (
    rows: Array<{ id: number; name?: string | null; value?: unknown }>,
    id: unknown
  ) => {
    const row = rows.find((r) => r.id === id);
    return row ? <span>{String(row.name ?? row.value ?? '')}</span> : null;
  };

  const airflowMin = ahu.min_airflow != null ? String(ahu.min_airflow) : '0';
  const airflowMax = ahu.max_airflow != null ? String(ahu.max_airflow) : '400000';

  /**
   * Special Options SELECT — open, and CLOSE on a second click.
   *
   * Legacy wires this button through a `SelectionModesHandler`
   * (tab.ahu.js:336-344, componentName 'special_options'), so the click runs
   * SelectionMode.changeToRatingMode (selection_mode.js:176-183):
   *   if (self.ratingModeActive && self.ratingDialogIsOpened())
   *     self.deactivateRatingMode(...)   // → Dispatcher 'rating_mode:hide'
   *   else self.activateRatingMode();    // → 'rating_mode:activate'
   * The React port only ever emitted `activate`, and the chassis treats an
   * activate for the already-open selector as a no-op (ComponentSelectorDialog
   * renderFromAHU), so a re-click did nothing and Cancel was the only way out.
   *
   * Same shape as the section forms' `useComponentForm.openRatingStub`:
   * `rating.open` is the per-component gate legacy keeps in `ratingModeActive`,
   * and `.main-rotation.rotate` is `ratingDialogIsOpened()` ported verbatim
   * (:185-187) so a stale gate cannot emit a `hide` with no dialog on screen.
   * The AHU tab has no `current_model`, so the chassis keys the gate on the
   * AHU id (renderFromAHU falls back to `ahu.id`) — compare against that.
   * `deactivateRatingMode` is called with force=false here (legacy's
   * `last_opened` is the AHU id it just published via `open_rating_popup`), so
   * the plain `rating_mode:hide` is the parity-correct emit.
   */
  const toggleSpecialOptions = () => {
    if (
      ratingOpen?.name === 'special_options' &&
      ratingOpen?.modelId === ahu.id &&
      document.querySelector('.main-rotation')?.classList.contains('rotate')
    ) {
      ahuBoardBus.emit('rating_mode:hide');
      return;
    }
    // SelectionModesHandler opens the Special Options selector
    // (component-selector family — 6e2 consumer; §33.10).
    ahuBoardBus.emit('rating_mode:activate', { name: 'special_options' });
  };

  return (
    <>
      <ul className="inputs-group" onFocusCapture={(e) => noteFocus(e.target)}>
        <li className="tab-header-info">
          <label className="wrap">
            <span className="label">Air Handling Unit</span>{' '}
            <span className="tag-field-wrap">
              <input
                type="text"
                name="tag"
                defaultValue={ahu.tag ?? ''}
                minLength={3}
                maxLength={30}
                /* dev-sync F (#823): dev relaxed the tag rule to any
                   3-30 characters and added the attribute to the board input
                   (tab.ahu.tmpl) as well as the New AHU popup. */
                pattern=".{3,30}"
                className={`ahu_field${tagNote ? ' invalid-field' : ''}`}
                tabIndex={-1}
                onInput={(e) => {
                  // dev-sync F (#823): dev's `changeTag` is bound to the INPUT
                  // event and runs `checkTagValidity(elem)` before mirroring
                  // (tab.ahu.js:523-529), so the red border + the min/max note
                  // appear on the FIRST keystroke — not a second later when the
                  // debounced save happens to run. Order matches dev
                  // (validity, then the #ahu-name mirror).
                  applyTagValidity(e.currentTarget.value);
                  onTagInput(e);
                  debouncedTag(e.currentTarget);
                }}
                onBlur={(e) => {
                  const i = e.currentTarget;
                  commitInput(i, () => commitTag(i.value));
                  // dev-sync F (#823) legacy `blurTag` (tab.ahu.js): leaving an
                  // invalid tag REVERTS the box to the stored value rather than
                  // stranding un-saveable text, re-runs validity against it, and
                  // puts the old tag back in the #ahu-name mirror.
                  if (!applyTagValidity(i.value)) {
                    const previousTag = ahu.tag ?? '';
                    i.value = previousTag;
                    applyTagValidity(previousTag);
                    document
                      .querySelectorAll('#ahu-name span.ahu-tag')
                      .forEach((el) => {
                        el.textContent = previousTag;
                      });
                  }
                }}
              />{' '}
              {tagNote === 'min' && (
                <span className="invalid-field-note minlength-note" style={{ display: 'inline' }}>
                  Min 3 characters.
                </span>
              )}
              {tagNote === 'max' && (
                <span className="invalid-field-note maxlength-note" style={{ display: 'inline' }}>
                  Max 30 characters.
                </span>
              )}
            </span>
          </label>
        </li>
      </ul>
      <ul
        className="inputs-group"
        id="ahu_tab_container"
        ref={scrollerRef}
        onFocusCapture={(e) => noteFocus(e.target)}
      >
        {/* ---- Tunnels ×N (§7.3 item 2) ---- */}
        {ahu.is_hybrid
          ? tunnelRowGroups.map((group) => (
              <li key={group.key} className="tunnel_row_group">
                <ul className="inputs-group">
                  <li>
                    <input
                      id={group.id}
                      className="sublist-ctrl-checkbox tunnel-row-toggle"
                      type="checkbox"
                      data-row-key={group.key}
                      checked={expandedRows.has(group.key)}
                      onChange={() => toggleRowGroup(group.key)}
                    />
                    <label htmlFor={group.id}>
                      {group.label}
                      <i className="icon-add" />
                    </label>
                    <ul className="sublist">
                      {group.tunnels.map((tunnel) =>
                        renderTunnelLi(tunnel, orderedTunnels.indexOf(tunnel))
                      )}
                    </ul>
                  </li>
                </ul>
              </li>
            ))
          : orderedTunnels.map((tunnel, idx) => renderTunnelLi(tunnel, idx))}

        {/* ---- Construction (§7.3 item 3) ---- */}
        <li>
          <input
            id="construction"
            className="subtab"
            type="checkbox"
            name="ahu_left_bar"
            checked={isOpen('construction')}
            onChange={() => toggle('construction')}
          />
          <label htmlFor="construction">
            Construction
            <i className="icon-add" />
          </label>
          <ul className="sublist">
            <li>
              <div className="single-line">
                <span className="selection">
                  {currentClass && <span>{String(currentClass.construction ?? currentClass.name ?? '')}</span>}
                </span>{' '}
                <select
                  name="construction_class_id"
                  required
                  className="ahu_field with-tooltip"
                  value={ahu.construction_class_id ?? ''}
                  onChange={onSelectField}
                >
                  {(constructionClasses ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {String(c.construction ?? c.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                {currentClass?.description ? (
                  <span className="select-tooltip">{currentClass.description}</span>
                ) : null}
                <span className="label">
                  Class
                  {currentClass?.description ? (
                    <span
                      className="ka-has-tooltip construction_class_desc"
                      {...{ tooltip: currentClass.description }}
                    >
                      <i className="icon-info" />
                    </span>
                  ) : null}
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="selection">
                  {selSpan(methods ?? [], ahu.ahu_construction_method_id)}
                </span>{' '}
                <select
                  name="ahu_construction_method_id"
                  required
                  className="ahu_field"
                  value={ahu.ahu_construction_method_id ?? ''}
                  onChange={onSelectField}
                >
                  {(methods ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {String(m.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Method</span>
              </div>
            </li>
            {ahu.ahu_construction_method_id === 2 && (
              <li>
                <div className="multiply-total multiply multiply-tunel ">
                  <span className="unit"> in</span>
                  {(
                    [
                      ['user_defined_module_max_width', 'width'],
                      ['user_defined_module_max_height', 'height'],
                      ['user_defined_module_max_length', 'length'],
                    ] as const
                  ).map(([field, dim], i) => (
                    <span key={field} style={{ display: 'contents' }}>
                      {/* legacy tmpl has whitespace around each element */}
                      {i > 0 && ' '}
                      {i > 0 && <span className="multiply-sign">x</span>}{' '}
                      <input
                        type="text"
                        data-type="number"
                        autoComplete="off"
                        name={field}
                        defaultValue={formatLegacyNumber(ahu[field], 0.01)}
                        data-min={framingDims[`cms_defined_module_min_${dim}`]}
                        data-step="0.01"
                        data-max={framingDims[`cms_defined_module_max_${dim}`]}
                        className="cnstr_field required-field small-input"
                        onBlur={(e) => onConstructionMethodField(e.currentTarget)}
                      />
                    </span>
                  ))}
                  <span className="label">
                    <span className="">Module Max W</span> x <span className="">H</span> x{' '}
                    <span>L</span>
                  </span>
                </div>
              </li>
            )}
            <li>
              <div>
                <div className="checkboxes-group d-inline-flex">
                  {weatherRatings.map((w) => (
                    <label key={w.id} className={`radio text-left ${String(w.name ?? '')}`}>
                      <input
                        type="radio"
                        name="weather_rating_id"
                        value={w.id}
                        checked={ahu.weather_rating_id === w.id}
                        className="ahu_field"
                        tabIndex={-1}
                        onChange={() => saveFields({ weather_rating_id: w.id })}
                      />{' '}
                      {String(w.name ?? '')}
                    </label>
                  ))}
                </div>{' '}
                <span className="label">Weather Rating</span>
              </div>
            </li>
            <li className="">
              <div className="single-line">
                <span className="selection">
                  <span>{String(ahu.floor_construction ?? '')}</span>
                </span>{' '}
                <select value={String(ahu.floor_construction ?? '')} onChange={() => undefined}>
                  <option value={String(ahu.floor_construction ?? '')}>
                    {String(ahu.floor_construction ?? '')}
                  </option>
                </select>{' '}
                <span className="label">Floor</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <input
                  id="roof_slope_settings"
                  type="checkbox"
                  className="mt-0 ahu_field"
                  name="roof_slope_settings"
                  tabIndex={-1}
                  checked={!!ahu.roof_slope_settings}
                  onChange={(e) => saveFields({ roof_slope_settings: e.currentTarget.checked })}
                />{' '}
                <label htmlFor="roof_slope_settings">Defaults</label>
                <span className="label">Roof </span>
              </div>
            </li>
            <li className={roofRowClass}>
              <div className="single-line">
                <span className="selection">{selSpan(roofShapes ?? [], roofStyleId)}</span>{' '}
                <select
                  name="roof_slope_style_id"
                  className="ahu_field"
                  value={roofStyleId ?? 'None'}
                  onChange={(e) => {
                    const id = selectValueToId(e.currentTarget.value);
                    // style change nulls orientation+direction (§7.3 item 3).
                    saveFields({
                      roof_slope_style_id: id,
                      roof_slope_orientation_id: null,
                      roof_slope_direction_id: null,
                    });
                  }}
                >
                  <option value="None" className="hide">
                    - -
                  </option>
                  {(roofShapes ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {String(r.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Style</span>
              </div>
            </li>
            <li className={roofRowClass}>
              <div className="single-line">
                <span className="selection">
                  {selSpan(roofOrientations ?? [], roofOrientationId)}
                </span>{' '}
                <select
                  name="roof_slope_orientation_id"
                  className="ahu_field"
                  value={roofOrientationId ?? 'None'}
                  onChange={(e) => {
                    const id = selectValueToId(e.currentTarget.value);
                    // orientation change nulls direction.
                    saveFields({
                      roof_slope_orientation_id: id,
                      roof_slope_direction_id: null,
                    });
                  }}
                >
                  <option value="None" className="hide">
                    - -
                  </option>
                  {(roofOrientations ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {String(r.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Slope Orientation</span>
              </div>
            </li>
            <li className={roofRowClass}>
              <div className="single-line">
                <span className="selection">
                  {selSpan(roofDirections ?? [], ahu.roof_slope_direction_id)}
                </span>{' '}
                <select
                  name="roof_slope_direction_id"
                  className="ahu_field"
                  value={(ahu.roof_slope_direction_id as number | null | undefined) ?? 'None'}
                  onChange={onSelectField}
                >
                  <option value="None" className="hide">
                    - -
                  </option>
                  {(roofDirections ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {String(r.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Slope Direction</span>
              </div>
            </li>
            <li className={roofRowClass}>
              <div>
                <span className="unit">%</span>{' '}
                <input
                  type="text"
                  name="roof_slope_pitch_percent"
                  defaultValue={String(ahu.roof_slope_pitch_percent ?? '')}
                  minLength={0}
                  maxLength={10}
                  className="ahu_field text-left"
                  onBlur={(e) => {
                    const i = e.currentTarget;
                    commitInput(i, () =>
                      saveFields({ roof_slope_pitch_percent: i.value || null })
                    );
                  }}
                />{' '}
                <span className="label">Slope Pitch</span>
              </div>
            </li>
            <li className={roofRowClass}>
              <div className="single-line">
                <span className="selection">{selSpan(roofLayers ?? [], ahu.roof_layers_id)}</span>{' '}
                <select
                  name="roof_layers_id"
                  className="ahu_field"
                  value={(ahu.roof_layers_id as number | null | undefined) ?? 'None'}
                  onChange={onSelectField}
                >
                  <option value="None">- -</option>
                  {(roofLayers ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {String(r.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Layers</span>
              </div>
            </li>
            {(
              [
                ['roof_top_layer_overhang', 'Top layer Overhang'],
                ['roof_seam_height', 'Seam Height'],
              ] as const
            ).map(([field, label]) => (
              <li key={field} className={roofRowClass}>
                <div>
                  <span className="unit">in</span>{' '}
                  <input
                    type="text"
                    name={field}
                    data-type="number"
                    defaultValue={formatLegacyNumber(ahu[field], 0.01)}
                    minLength={0}
                    maxLength={36}
                    className="ahu_field text-left"
                    data-step="0.01"
                    onBlur={(e) => onNumberField(e.currentTarget)}
                    onInput={(e) => debouncedNumberField(e.currentTarget)}
                  />{' '}
                  <span className="label">{label}</span>
                </div>
              </li>
            ))}
            <li className={roofRowClass}>
              <div className="single-line">
                <span className="selection">
                  {selSpan(roofSeamDirections ?? [], ahu.roof_seam_direction_id)}
                </span>{' '}
                <select
                  name="roof_seam_direction_id"
                  className="ahu_field"
                  value={(ahu.roof_seam_direction_id as number | null | undefined) ?? 'None'}
                  onChange={onSelectField}
                >
                  <option value="None">- -</option>
                  {(roofSeamDirections ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {String(r.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Seam Direction</span>
              </div>
            </li>
            <li className={roofRowClass}>
              <div>
                <span className="unit">in</span>{' '}
                <input
                  type="text"
                  name="roof_seam_flange_width"
                  defaultValue={formatLegacyNumber(ahu.roof_seam_flange_width, 0.01)}
                  minLength={0}
                  maxLength={36}
                  data-type="number"
                  className="ahu_field text-left"
                  data-step="0.01"
                  onBlur={(e) => onNumberField(e.currentTarget)}
                  onInput={(e) => debouncedNumberField(e.currentTarget)}
                />{' '}
                <span className="label">Seam Flange Width</span>
              </div>
            </li>
            <li className={roofRowClass}>
              <div>
                <span className="unit">in</span>{' '}
                <input
                  id="overall_height"
                  readOnly
                  type="text"
                  data-type="decimal"
                  name="overall_height"
                  value={String(ahu.overall_height ?? "")}
                  className="ahu_field text-left"
                  onChange={() => undefined}
                />{' '}
                <span className="label">AHU Height Overall</span>
              </div>
            </li>
          </ul>
        </li>

        {/* ---- Base & Lugs (§7.3 item 4) ---- */}
        {ahu.is_hybrid ? (
          <>
            <li id="base-lugs" className="base-lugs-panel">
              <input
                id="base_panel"
                className="subtab"
                type="checkbox"
                name="ahu_left_bar"
                checked={isOpen('base')}
                onChange={() => toggle('base')}
              />
              <label htmlFor="base_panel">
                <span className="base_and_lugs_label">Base</span>
                <i className="icon-add" />
              </label>
              <BasePanel
                tunnels={tunnels}
                activeTunnelId={hybridActiveTunnelId}
                cms={cms}
                open={isOpen('base')}
              />
            </li>
            <li id="lifting-lugs" className="base-lugs-panel">
              <input
                id="lifting_lugs_panel"
                className="subtab"
                type="checkbox"
                name="ahu_left_bar"
                // Shares the 'lugs' accordion key with the non-hybrid panel
                // (not a hybrid-specific 'lifting-lugs' key): AhuCanvas.tsx
                // dispatches setAhuTabAccordion('lugs') when a lug is picked
                // on the canvas, and that trigger must keep opening whichever
                // panel actually holds the lug editor for this AHU.
                checked={isOpen('lugs')}
                onChange={() => toggle('lugs')}
              />
              <label htmlFor="lifting_lugs_panel">
                <span className="base_and_lugs_label">Lifting Lugs</span>
                <i className="icon-add" />
              </label>
              <LiftingLugPane
                ahu={ahu}
                tunnels={tunnels}
                cms={cms}
                open={isOpen('lugs')}
                hybridActiveTunnelId={hybridActiveTunnelId}
              />
            </li>
          </>
        ) : (
          <li id="base-lugs" className="base-lugs-panel">
            <input
              id="lugs"
              className="subtab"
              type="checkbox"
              name="ahu_left_bar"
              checked={isOpen('lugs')}
              onChange={() => toggle('lugs')}
            />
            <label htmlFor="lugs">
              <span className="base_and_lugs_label">Base &amp; Lugs</span>
              <i className="icon-add" />
            </label>
            <LiftingLugPane ahu={ahu} tunnels={tunnels} cms={cms} open={isOpen('lugs')} />
          </li>
        )}

        {/* ---- Properties (§7.3 item 5) ---- */}
        <li>
          <input
            id="properties"
            className="subtab"
            type="checkbox"
            name="ahu_left_bar"
            checked={isOpen('properties')}
            onChange={() => toggle('properties')}
          />
          <label htmlFor="properties">
            Properties
            <i className="icon-add" />
          </label>
          <ul className="sublist">
            <li>
              <div className="">
                <input
                  type="text"
                  data-type="number"
                  autoComplete="off"
                  name="qty"
                  defaultValue={formatLegacyNumber(ahu.qty, 1)}
                  data-min="1"
                  data-step="1"
                  data-max="999"
                  className="ahu_field text-left with-padding-right"
                  onBlur={(e) => onNumberField(e.currentTarget)}
                  onInput={(e) => debouncedNumberField(e.currentTarget)}
                />{' '}
                <span className="label">
                  <span> Qty</span>
                </span>
              </div>
            </li>
            {(
              [
                ['model', 'Model'],
                ['serial', 'Serial'],
              ] as const
            ).map(([field, label]) => (
              <li key={field}>
                <div className="with-tooltip">
                  <input
                    type="text"
                    defaultValue={String(ahu[field] ?? '')}
                    name={field}
                    minLength={0}
                    maxLength={50}
                    className="ahu_field text-left with-padding-right ellipsis"
                    onBlur={(e) => {
                      const i = e.currentTarget;
                      commitInput(i, () => saveFields({ [field]: i.value || null }));
                    }}
                  />
                  <i
                    className="text-clear icon-close-1"
                    onClick={(e) => {
                      const input = e.currentTarget
                        .parentElement?.querySelector<HTMLInputElement>('input');
                      if (input) input.value = '';
                      saveFields({ [field]: null });
                      // QA6 #2 — the clear IS the commit; re-baseline so the
                      // blur that follows does not send a second identical PATCH.
                      if (input) rebase(input);
                    }}
                  />{' '}
                  <span className="label">{label}</span>
                </div>
              </li>
            ))}
            <li>
              <div className="single-line">
                <span className="selection">{selSpan(airSystems, ahu.air_system_id)}</span>{' '}
                <select
                  name="air_system_id"
                  required
                  className="ahu_field"
                  value={ahu.air_system_id ?? 'None'}
                  onChange={onSelectField}
                >
                  <option value="None">- -</option>
                  {airSystems.map((a) => (
                    <option key={a.id} value={a.id}>
                      {String(a.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Air System</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="selection">{selSpan(configurations, ahu.configuration_id)}</span>{' '}
                <select
                  name="configuration_id"
                  required
                  className="ahu_field"
                  value={ahu.configuration_id ?? 'None'}
                  onChange={onSelectField}
                >
                  <option value="None">- -</option>
                  {configurations.map((c) => (
                    <option key={c.id} value={c.id}>
                      {String(c.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Duct System</span>
              </div>
            </li>
            {(
              [
                ['location', 'Location'],
                ['service', 'Service'],
              ] as const
            ).map(([field, label]) => (
              <li key={field}>
                <div className="with-tooltip">
                  <input
                    type="text"
                    name={field}
                    defaultValue={String(ahu[field] ?? '')}
                    minLength={0}
                    maxLength={50}
                    className="ahu_field text-left with-padding-right ellipsis"
                    onBlur={(e) => {
                      const i = e.currentTarget;
                      commitInput(i, () => saveFields({ [field]: i.value || null }));
                    }}
                  />
                  <i
                    className="text-clear icon-close-1"
                    onClick={(e) => {
                      const input = e.currentTarget
                        .parentElement?.querySelector<HTMLInputElement>('input');
                      if (input) input.value = '';
                      saveFields({ [field]: null });
                      // QA6 #2 — the clear IS the commit; re-baseline so the
                      // blur that follows does not send a second identical PATCH.
                      if (input) rebase(input);
                    }}
                  />{' '}
                  <span className="label">{label}</span>
                </div>
              </li>
            ))}
            <li className="certification-group" ref={certGroupRef}>
              <div>
                <button
                  id="show_certifications"
                  type="button"
                  className="icon-mounting icon-selection"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (certListOpen) certCommit();
                    else setCertListOpen(true);
                  }}
                />{' '}
                <div className={`certification-edit-list${certListOpen ? ' active' : ''}`}>
                  <ul>
                    {cmsCerts.map((c) => (
                      <li key={c.id}>
                        <label>
                          <input
                            type="checkbox"
                            value={c.id}
                            className="certification"
                            name="ahu_certification_etl"
                            defaultChecked={(certifications ?? []).some((cur) => cur.id === c.id)}
                            tabIndex={-1}
                            onChange={(e) => e.currentTarget.classList.add('changed')}
                          />{' '}
                          <span className="label">{String(c.name ?? '')}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>{' '}
                <span className="checked-certifications output">
                  {/* QA5 #5 — legacy renders whitespace between these spans
                      (tab.ahu.tmpl:418-426); the CSS `span+span::before`
                      comma (margin-left:-0.25em) is designed to overlap that
                      gap — without it the comma jams into the text. */}
                  {(certifications ?? []).map((c) => (
                    <Fragment key={c.id}>
                      {' '}
                      <span>{String(c.name ?? '')}</span>
                    </Fragment>
                  ))}{' '}
                </span>{' '}
                <span className="label">Certification</span>
              </div>
            </li>
          </ul>
        </li>

        {/* ---- Performance (§7.3 item 6) ---- */}
        <li>
          <input
            id="performance"
            className="subtab"
            type="checkbox"
            name="ahu_left_bar"
            checked={isOpen('performance')}
            onChange={() => toggle('performance')}
          />
          <label htmlFor="performance">
            Performance
            <i className="icon-add" />
          </label>
          <ul className="sublist">
            <li>
              <div className="airflow-control">
                <span className="unit">CFM</span>{' '}
                <input
                  type="text"
                  data-type="number"
                  autoComplete="off"
                  name="sa_volume_max"
                  defaultValue={formatLegacyNumber(ahu.sa_volume_max, 1)}
                  data-min={airflowMin}
                  data-max={airflowMax}
                  data-step="1"
                  className="ahu_field required-field medium-input integer"
                  onBlur={(e) => onNumberField(e.currentTarget)}
                  onInput={(e) => debouncedNumberField(e.currentTarget)}
                />{' '}
                <span className="selection">
                  {selSpan(saControls, ahu.sa_volume_control_id)}
                </span>{' '}
                <select
                  name="sa_volume_control_id"
                  required
                  className="ahu_field medium-input"
                  value={(ahu.sa_volume_control_id as number | null | undefined) ?? 'None'}
                  onChange={onSelectField}
                >
                  <option value="None">- -</option>
                  {saControls.map((c) => (
                    <option key={c.id} value={c.id}>
                      {String(c.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">
                  <span>Supply Airflow</span> |<span> Control</span>
                </span>
              </div>
            </li>
            {showDecks && (
              <li>
                <div className="frequency">
                  <span className="unit">CFM</span>{' '}
                  <input
                    className="medium-input ahu_field"
                    type="text"
                    data-type="number"
                    autoComplete="off"
                    name="sa_volume_hdeck"
                    defaultValue={formatLegacyNumber(ahu.sa_volume_hdeck, 1)}
                    data-min="0"
                    data-step="1"
                    data-max="400000"
                    onBlur={(e) => onNumberField(e.currentTarget)}
                    onInput={(e) => debouncedNumberField(e.currentTarget)}
                  />{' '}
                  <input
                    className="medium-input ahu_field"
                    type="text"
                    data-type="number"
                    autoComplete="off"
                    name="sa_volume_cdeck"
                    defaultValue={formatLegacyNumber(ahu.sa_volume_cdeck, 1)}
                    data-min="0"
                    data-step="1"
                    data-max="400000"
                    onBlur={(e) => onNumberField(e.currentTarget)}
                    onInput={(e) => debouncedNumberField(e.currentTarget)}
                  />{' '}
                  <span className="label">Airflow Decks Hot | Cold</span>
                </div>
              </li>
            )}
            {(
              [
                ['oa_volume_min', 'oa_volume_max', 'Outside Airflow'],
                ['ra_volume_min', 'ra_volume_max', 'Return Airflow'],
                ['ea_volume_min', 'ea_volume_max', 'Exhaust Airflow'],
              ] as const
            ).map(([minField, maxField, label]) => (
              <li key={minField}>
                <div className="frequency">
                  <span className="unit">CFM</span>{' '}
                  <input
                    type="text"
                    data-type="number"
                    autoComplete="off"
                    className="medium-input ahu_field "
                    name={minField}
                    defaultValue={formatLegacyNumber(ahu[minField], 1)}
                    data-min={airflowMin}
                    data-max={airflowMax}
                    data-step="1"
                    placeholder="min"
                    onBlur={(e) => onNumberField(e.currentTarget)}
                    onInput={(e) => debouncedNumberField(e.currentTarget)}
                  />{' '}
                  <input
                    type="text"
                    data-type="number"
                    autoComplete="off"
                    className="medium-input ahu_field "
                    name={maxField}
                    defaultValue={formatLegacyNumber(ahu[maxField], 1)}
                    data-min={airflowMin}
                    data-max={airflowMax}
                    data-step="1"
                    placeholder="max"
                    onBlur={(e) => onNumberField(e.currentTarget)}
                    onInput={(e) => debouncedNumberField(e.currentTarget)}
                  />{' '}
                  <span className="label">{label}</span>
                </div>
              </li>
            ))}
          </ul>
        </li>

        {/* ---- Electrical (§7.3 item 7) ---- */}
        <li>
          <input
            id="electrical"
            className="subtab"
            type="checkbox"
            name="ahu_left_bar"
            checked={isOpen('electrical')}
            onChange={() => toggle('electrical')}
          />
          <label htmlFor="electrical">
            Electrical
            <i className="icon-add" />
          </label>
          <ul className="sublist">
            <li>
              <div className="single-line">
                <span className="selection">{selSpan(voltages ?? [], ahu.ahu_main_voltage_id)}</span>{' '}
                <select
                  name="ahu_main_voltage_id"
                  className="ahu_field"
                  value={(ahu.ahu_main_voltage_id as number | null | undefined) ?? 'None'}
                  onChange={onSelectField}
                >
                  <option value="None" className="hide">
                    - -
                  </option>
                  {(voltages ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      {String(v.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Main Voltage</span>
              </div>
            </li>
            {(
              [
                ['ahu_xfmr_115v_rated_va_id', ratedVa115, 'Desired Transformer - 120V'],
                ['ahu_xfmr_24v_rated_va_id', ratedVa24, 'Desired Transformer - 24V'],
              ] as const
            ).map(([field, rows, label]) => (
              <li key={field}>
                <div>
                  <span className="unit">VA</span>{' '}
                  <span className="selection">{selSpan(rows ?? [], ahu[field])}</span>{' '}
                  <select
                    name={field}
                    className="ahu_field"
                    value={(ahu[field] as number | null | undefined) ?? 'None'}
                    onChange={onSelectField}
                  >
                    <option value="None" className="">
                      - -
                    </option>
                    {(rows ?? []).map((r) => (
                      <option key={r.id} value={r.id}>
                        {String(r.value ?? '')}
                      </option>
                    ))}
                  </select>{' '}
                  <span className="label">{label}</span>
                </div>
              </li>
            ))}
          </ul>
        </li>

        {/* ---- Special Options (§7.3 item 8 — selector open = 6e2 mount) ---- */}
        <li>
          <label htmlFor="special_options_0">Special Options</label>
          <ul className="sublist special_options_sublist ">
            <li className="select-special-options">
              <input
                type="radio"
                name="select_mode_id"
                className="select_mode_radio hidden"
                data-id="2"
                data-value="rating"
                data-name="special_options"
                defaultChecked
              />
              <span className="open-rating" onClick={toggleSpecialOptions}>
                <i className="icon-rating" />{' '}
                SELECT
              </span>
            </li>
          </ul>
        </li>

        {/* ---- Remarks (§7.3 item 9) ---- */}
        <li>
          <input
            id="remarks"
            className="subtab"
            type="checkbox"
            name="ahu_left_bar"
            checked={isOpen('remarks')}
            onChange={() => toggle('remarks')}
          />
          <label htmlFor="remarks">
            Remarks
            <i className="icon-add" />{' '}
            <span className="short_remarks">{String(ahu.remarks ?? '')}</span>
          </label>
          <ul className="sublist">
            <li>
              <div
                {...{ maxlength: 1000, placeholder: '1000 characters maximum' }}
                className="textarea remarks ahu_field remarks-area"
                {...{ name: 'remarks' }}
                contentEditable
                suppressContentEditableWarning
                onBlur={onRemarksBlur}
              >
                {String(ahu.remarks ?? '')}
              </div>
            </li>
          </ul>
        </li>
      </ul>
    </>
  );
};

export default TabAhu;
